import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Layout, Tabs } from 'tea-component';
import '@/pages/opinion/assets/scss/pages/community/video.scss';
import TipContext from '@/pages/home/components/TipContextComponents/TipContext';
import { connect } from 'umi';
import BubbleCommon, {
  videoHeadText,
} from '@/pages/opinion/components/BubbleCommon';
import ContentBody from '@/pages/opinion/components/ContentBody';
import useTabHistory from '@op/hooks/useTabHistory';
import useLanguage from '@op/hooks/useLanguage';
import VideoStatistics from './pages/VideStatistics';
import VideoTagInsight from './pages/VideoTagInsight';
import CardCommon from '@op/components/CardCommon';
import classnames from 'classnames';
import { useFilterCondConf } from '@op/hooks/useFilterCondConf';
import { AI_TAGS_COLORS } from './components/TagCard';
import { getAITagColorKey } from './utils';

const { Content } = Layout;
interface IAITagColorMap {
  [key: string]: { color: string; backgroundColor: string; };
}
interface IVideoProps {
  gameInfo: any;
  userInfo: any;
  wholeGameInfo: any;
  userInfoLoading: boolean;
}

function Video(props: IVideoProps) {
  const { formatMessage } = useLanguage();
  const { gameInfo } = props;
  const scrollBody = useRef<HTMLElement | null>(null);
  const handleScrollRef = useRef<((e: any) => void) | null>(null);
  const { loading: pageLoading, is_support_video_tags: isSupportVideoTags, video_tags: videoTags } = useFilterCondConf({
    gameInfo,
    needRequest: true,
    requestList: [{
      key: 'is_support_video_tags',
      defaultValue: false
    },
    {
      key: 'video_tags',
      defaultValue: []
    }],
  });

  const { tab, onActive } = useTabHistory({
    defaultValue: 'statistics',
  });

  const tabs = useMemo(
    () => [
      {
        id: 'statistics',
        label: formatMessage({ id: 'Statistics' }),
      },
      {
        id: 'insight',
        label: formatMessage({ id: 'Video Tag Insight' }),
      },
    ],
    [formatMessage],
  );

  // 在组件挂载后，找到实际的滚动容器
  useEffect(() => {
    try {
      const aiTagColorKey = getAITagColorKey();
      const localColor = sessionStorage.getItem(aiTagColorKey);
      if (localColor) {
        return;
      }
      if (videoTags?.length > 0) {
        const colorMap: IAITagColorMap = {};
        videoTags.forEach((item: { label_en: string; }, index: number) => {
          colorMap[item.label_en] = AI_TAGS_COLORS[index];
        });
        sessionStorage.setItem(aiTagColorKey, JSON.stringify(colorMap));
      }
    } catch (error) {
      console.warn('Failed to access localStorage:', error);
    }
  }, [videoTags]);

  const handleScroll = useCallback((e: any) => {
    // 只在 insight tab 时执行滚动处理
    if (tab === 'insight' && handleScrollRef.current) {
      handleScrollRef.current(e);
    }
  }, [tab]);

  return (
    <Content className="dbo-page dbo-video" ref={scrollBody} onScrollCapture={handleScroll}>
      <ContentBody
        contentHeard={
          <Content.Header
            className={isSupportVideoTags ? "dbo-header contentHead-normal" : "dbo-header"}
            title={formatMessage({ id: 'Video' })}
            subtitle={
              <div className="dbo-header-subtitle ">
                {formatMessage({ id: 'Data Update' })}
                <BubbleCommon showIcon={false} tipsMap={videoHeadText}>
                  <TipContext className="title-tip" placement="bottom" />
                </BubbleCommon>
              </div>
            }
            operation={
              <>
                {
                  isSupportVideoTags && (
                    <div className='dbo-header-operation'>
                      {formatMessage({ id: 'The AI tags are supported by the Data & AI team. For more information, please contact zhenquanlin@tencent.com.' })}
                    </div>
                  )
                }
              </>
            }
          >
            {
              isSupportVideoTags && (
                <div className="header-tab">
                  <Tabs
                    ceiling
                    tabs={tabs}
                    animated={false}
                    activeId={tab}
                    onActive={(tab) => {
                      onActive(tab);
                    }}
                  />
                </div>
              )
            }
          </Content.Header>
        }
      >
        <CardCommon
          className={classnames({
            'dbo-video-whole-screen-loading': pageLoading,
          })}
          loading={pageLoading}
          contentCard
        >
          <>
            {tab === 'statistics' && (
              <VideoStatistics
                {...props}
                isSupportVideoTags={isSupportVideoTags}
              />
            )}

            {tab === 'insight' && (
              <VideoTagInsight
                {...props}
                isSupportVideoTags={isSupportVideoTags}
                scrollBody={scrollBody}
                onScrollHandlerReady={(handler) => {
                  handleScrollRef.current = handler;
                }}
              />
            )}
          </>
        </CardCommon>
      </ContentBody>
    </Content>
  );
}

export default connect((state: any) => {
  const { loading, home, opinionCommon } = state;
  return {
    userInfoLoading: loading.effects['home/getUserInfo'],
    userInfo: home.userInfo,
    gameInfo: opinionCommon.gameInfo,
    wholeGameInfo: opinionCommon.wholeGameInfo,
  };
})(Video);
